/**
 * Shared Excel utilities — extracted from export-excel.ts for Phase 14.
 *
 * Colour palette, number formats, cell styling helpers, formula helpers.
 * All sheet builders import from this module.
 */

import type { Cell, Worksheet } from "exceljs";
import tokens from "../design/tokens.json";
import { resolveNumFmt } from "../excel-validation/unit-types";

/* ── Colour palette ── */
export const C = {
  inputBlue: "0000FF",
  formulaBlack: "000000",
  linkGreen: "008000",
  headerBg: tokens.report.ink.slice(1).toUpperCase(),
  headerText: "FFFFFF",
  assumptionYellow: "FFFF00",
  passGreen: "C6EFCE",
  passGreenText: "006100",
  failRed: "FFC7CE",
  failRedText: "9C0006",
  warnAmber: "FFEB9C",
  warnAmberText: "9C5700",
  subtotalGrey: tokens.report.pageBg.slice(1).toUpperCase(),
  mutedGrey: tokens.report.muted.slice(1).toUpperCase(),
  border: tokens.report.border.slice(1).toUpperCase(),
  white: "FFFFFF",
} as const;

/* ── Number formats ── */
export const NF = {
  currency: '$#,##0;($#,##0);"-"',
  currencyCents: '$#,##0.00;($#,##0.00);"-"', // fail-evidence rows whose check trips at 1¢
  currencyM: '$0.00,,"ᴍ";($0.00,,"ᴍ");"-"',
  pct: "0.0%",
  pct2: "0.00%",
  num: "#,##0",
  num1: "#,##0.0",
  multiple: "0.0x",
  text: "@",
} as const;

/* ── Styling helpers ── */
export function styleHeader(cell: Cell) {
  cell.font = {
    bold: true,
    color: { argb: C.headerText },
    size: 11,
    name: "Arial",
  };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.headerBg },
  };
  cell.alignment = { horizontal: "left", vertical: "middle" };
  cell.border = {
    top: { style: "thin", color: { argb: C.border } },
    bottom: { style: "thin", color: { argb: C.border } },
    left: { style: "thin", color: { argb: C.border } },
    right: { style: "thin", color: { argb: C.border } },
  };
}

export function styleInput(cell: Cell, isKey = false) {
  cell.font = { color: { argb: C.inputBlue }, name: "Arial", size: 10 };
  if (isKey) {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: C.assumptionYellow },
    };
  }
}

export function styleFormula(cell: Cell) {
  cell.font = { color: { argb: C.formulaBlack }, name: "Arial", size: 10 };
}

export function styleLink(cell: Cell) {
  cell.font = { color: { argb: C.linkGreen }, name: "Arial", size: 10 };
}

export function styleLabel(cell: Cell, bold = false) {
  cell.font = {
    bold,
    color: { argb: C.formulaBlack },
    name: "Arial",
    size: 10,
  };
  cell.alignment = { horizontal: "left", vertical: "middle" };
}

export function styleNote(cell: Cell, colorArgb: string = C.formulaBlack) {
  cell.font = {
    italic: true,
    color: { argb: colorArgb },
    name: "Arial",
    size: 9,
  };
}

export function stylePass(cell: Cell) {
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.passGreen },
  };
  cell.font = {
    bold: true,
    color: { argb: C.passGreenText },
    name: "Arial",
    size: 10,
  };
}

export function styleWarn(cell: Cell) {
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.warnAmber },
  };
  cell.font = {
    bold: true,
    color: { argb: C.warnAmberText },
    name: "Arial",
    size: 10,
  };
}

export function styleFail(cell: Cell) {
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.failRed },
  };
  cell.font = {
    bold: true,
    color: { argb: C.failRedText },
    name: "Arial",
    size: 10,
  };
}

export function styleTotal(cell: Cell) {
  cell.font = {
    bold: true,
    color: { argb: C.formulaBlack },
    name: "Arial",
    size: 10,
  };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.subtotalGrey },
  };
}

export function thinBorder(cell: Cell) {
  cell.border = {
    top: { style: "thin", color: { argb: C.border } },
    bottom: { style: "thin", color: { argb: C.border } },
    left: { style: "thin", color: { argb: C.border } },
    right: { style: "thin", color: { argb: C.border } },
  };
}

export function thinBorderRow(
  ws: Worksheet,
  row: number,
  colStart: number,
  colEnd: number,
) {
  for (let c = colStart; c <= colEnd; c++) thinBorder(ws.getCell(row, c));
}

/* ── Formula helpers ── */

/** Auto-detect number format from label text — delegates to explicit type mapping. */
export function autoNumFmt(
  label: string,
  value: unknown,
  assumptionKey?: string,
): string | undefined {
  return resolveNumFmt(label, value, assumptionKey);
}

/** Set cell to a formula with pre-computed result fallback. The cached result is usually a number,
 *  but may be a string (e.g. "n/a") for IFERROR-wrapped formulas that can recalc to text. */
export function normalizeFormulaExpression(formula: string): string {
  const trimmed = formula.trim();
  const expression = trimmed.startsWith("=")
    ? trimmed.slice(1).trimStart()
    : trimmed;
  return expression.startsWith("=") ? "" : expression;
}

export function setFormula(
  cell: Cell,
  formula: string,
  result: number | string,
  opts?: { numFmt?: string; bold?: boolean; isLink?: boolean },
) {
  const normalized = normalizeFormulaExpression(formula);
  if (!normalized) {
    cell.value = result;
    styleFormula(cell);
  } else {
    cell.value = { formula: normalized, result };
    if (opts?.isLink) styleLink(cell);
    else styleFormula(cell);
  }
  if (opts?.numFmt) cell.numFmt = opts.numFmt;
  if (opts?.bold && cell.font) cell.font = { ...cell.font, bold: true };
  thinBorder(cell);
}

export function fmtMoney(v: number | null | undefined): number | string {
  if (v == null || !Number.isFinite(v)) return "—";
  const n = Math.round(v);
  return n === 0 ? 0 : n;
}

/** Board-copy currency formatter. Keeps financial cells numeric elsewhere. */
export function fmtCompactMoney(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const absolute = Math.abs(value);
  const sign = value < 0 ? "−" : "";
  if (absolute >= 1_000_000_000) {
    return `${sign}$${(absolute / 1_000_000_000).toFixed(1)}bn`;
  }
  if (absolute >= 1_000_000) {
    return `${sign}$${(absolute / 1_000_000).toFixed(1)}m`;
  }
  if (absolute >= 1_000) {
    return `${sign}$${(absolute / 1_000).toFixed(1)}k`;
  }
  return `${sign}$${Math.round(absolute).toLocaleString("en-AU")}`;
}

export function fmtPct(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${(v * 100).toFixed(2)}%`;
}

/** Convert 0-based (row, col) to A1 notation. Row 0 = Excel row 1. */
export function addr(row: number, col: number): string {
  let colStr = "";
  let c = col;
  do {
    colStr = String.fromCodePoint(65 + (c % 26)) + colStr;
    c = Math.floor(c / 26) - 1;
  } while (c >= 0);
  return `${colStr}${row + 1}`;
}

export const AU_DATE = (d = new Date()) =>
  d.toLocaleDateString("en-AU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
